import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const QUICK_IDEA_KEY = "quickIdeaMode";
const REGULAMENTO_ID_KEY = "quickIdeaRegulamentoId";

type QuickIdeaState = {
  isQuickMode: boolean;
  regulamentoId: string | null;
  clearQuickMode: () => void;
};

/**
 * Hook to detect and manage quick idea mode from URL params
 * When user accesses via QR Code, saves state to sessionStorage
 * so the modal can be opened after login
 */
function useQuickIdeaMode(): QuickIdeaState {
  const [searchParameters, setSearchParameters] = useSearchParams();

  // Check URL params first
  const urlQuickIdea = searchParameters.get("quickIdea") === "true";
  const urlRegulamentoId = searchParameters.get("regulamentoId");

  // If URL has quick mode, save to sessionStorage
  useEffect(() => {
    if (urlQuickIdea && urlRegulamentoId) {
      sessionStorage.setItem(QUICK_IDEA_KEY, "true");
      sessionStorage.setItem(REGULAMENTO_ID_KEY, urlRegulamentoId);

      // Clean URL params after saving
      searchParameters.delete("quickIdea");
      searchParameters.delete("regulamentoId");
      setSearchParameters(searchParameters, { replace: true });
    }
  }, [urlQuickIdea, urlRegulamentoId, searchParameters, setSearchParameters]);

  // Read from sessionStorage
  const isQuickMode = sessionStorage.getItem(QUICK_IDEA_KEY) === "true";
  const regulamentoId = sessionStorage.getItem(REGULAMENTO_ID_KEY);

  // Clear quick mode after use
  const clearQuickMode = useCallback(() => {
    sessionStorage.removeItem(QUICK_IDEA_KEY);
    sessionStorage.removeItem(REGULAMENTO_ID_KEY);
  }, []);

  return {
    isQuickMode,
    regulamentoId,
    clearQuickMode
  };
}

export default useQuickIdeaMode;
